/**
 * Tipo Appointments booking overrides for sage-theme.
 *
 * Ports the two runtime patches that the live Vessel theme uses to make the
 * Tipo "Book Now" flow work correctly on Sage & Story's reservation products.
 * Source of truth & rationale: TIPO_BOOKING_TROUBLESHOOTING_RUNBOOK.md
 *
 * 1. Guest bookings: force use_customer_login = false so Tipo doesn't redirect
 *    anonymous shoppers to the login screen.
 * 2. Slot availability for product-basis services: rewrite Tipo
 *    /api/front/slots requests so they query the same capacity bucket
 *    (employeeId=0) where bookings are actually stored.
 *
 * The script is theme-agnostic: it looks up the current product via any
 * element carrying data-tipo-product-id. Activate Tipo on a page by adding
 * that attribute to the product form wrapper.
 */

(function () {
  function getProductIdFromDom() {
    const el = document.querySelector("[data-tipo-product-id]");
    return el ? el.getAttribute("data-tipo-product-id") : null;
  }

  function getTipoBookingContext() {
    const productId = getProductIdFromDom();
    const booking = window.Tipo && window.Tipo.Booking;
    const bookingProducts = booking && booking.configs && booking.configs.products;

    if (!productId || !Array.isArray(bookingProducts)) {
      return null;
    }

    const service = bookingProducts.find(
      (entry) => entry && String(entry.id) === String(productId)
    );
    if (!service) {
      return null;
    }

    return {
      booking: booking,
      productId: String(productId),
      service: service,
    };
  }

  function disableTipoCustomerLoginForBookingProduct() {
    const context = getTipoBookingContext();
    if (!context) {
      return false;
    }

    context.booking.use_customer_login = false;

    if (context.booking.settings && context.booking.settings.general) {
      context.booking.settings.general.use_customer_login = false;
    }

    return true;
  }

  function normalizeTipoProductAvailabilitySlotRequests() {
    const context = getTipoBookingContext();
    if (!context || !context.service || context.service.available_time_basis !== "product") {
      return false;
    }

    if (window.__tipoProductAvailabilitySlotsPatched) {
      return true;
    }

    const nativeFetch = window.fetch && window.fetch.bind(window);
    if (typeof nativeFetch !== "function") {
      return false;
    }

    let bookingAppOrigin;
    try {
      bookingAppOrigin = new URL(context.booking.appUrl, window.location.origin).origin;
    } catch (_error) {
      return false;
    }

    function shouldRewriteSlotsRequest(url) {
      return (
        url.origin === bookingAppOrigin &&
        url.pathname === "/api/front/slots" &&
        url.searchParams.get("productId") === context.productId &&
        url.searchParams.get("employeeId") !== "0"
      );
    }

    window.fetch = function tipoBookingFetch(input, init) {
      try {
        if (input instanceof Request) {
          const url = new URL(input.url, window.location.origin);
          if (shouldRewriteSlotsRequest(url)) {
            url.searchParams.set("employeeId", "0");
            return nativeFetch(new Request(url.toString(), input), init);
          }
        } else if (typeof input === "string" || input instanceof URL) {
          const url = new URL(String(input), window.location.origin);
          if (shouldRewriteSlotsRequest(url)) {
            url.searchParams.set("employeeId", "0");
            return nativeFetch(url.toString(), init);
          }
        }
      } catch (_error) {
        // Fall through to the native fetch if the request cannot be normalized.
      }

      return nativeFetch(input, init);
    };

    window.__tipoProductAvailabilitySlotsPatched = true;
    return true;
  }

  function applyTipoBookingOverrides() {
    const didDisableLogin = disableTipoCustomerLoginForBookingProduct();
    const didNormalizeSlots = normalizeTipoProductAvailabilitySlotRequests();
    return didDisableLogin || didNormalizeSlots;
  }

  // No product on this page → no work to do.
  if (!getProductIdFromDom()) {
    return;
  }

  applyTipoBookingOverrides();

  // Tipo's embed script loads asynchronously; poll briefly until it's ready.
  let attempts = 0;
  const intervalId = window.setInterval(function () {
    attempts += 1;
    if (applyTipoBookingOverrides() || attempts >= 120) {
      window.clearInterval(intervalId);
    }
  }, 250);

  // Re-apply right before the shopper takes the action that depends on Tipo
  // being correctly configured (belt-and-suspenders against late-init races).
  document.addEventListener(
    "click",
    function (event) {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('.tpb-book-button, button[name="add"]')
      ) {
        applyTipoBookingOverrides();
      }
    },
    true
  );
})();
